import React, { useState, useEffect } from "react";
import { TrendingUp } from "lucide-react";
import { toast } from "react-toastify";
import { getTopApiUsers } from "../../../../services/apiAndBillingService";

const TopAPIUsers = () => {
  const [topUsers, setTopUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    const fetchTopApiUsers = async () => {
      try {
        setLoading(true);
        const data = await getTopApiUsers(6); // Get top 6 users
        setTopUsers(data.topApiUsers || []);
      } catch (err) {
        toast.error(`Failed to load top API users: ${err.message}`);
        console.error('Error fetching top API users:', err);
      } finally {
        setLoading(false);
      }
    };

    fetchTopApiUsers();
  }, []);

  const getPlanTypeColor = (planType) => {
    switch (planType.toLowerCase()) {
      case "enterprise":
        return "bg-purple-100 text-purple-800";
      case "pro":
        return "bg-blue-100 text-blue-800";
      case "starter":
        return "bg-yellow-100 text-yellow-800";
      default:
        return "bg-gray-100 text-gray-800";
    }
  };

  const formatCurrency = (amount) => {
    return `$${amount.toLocaleString()}`;
  };

  const formatRequests = (requests) => {
    if (requests >= 1000000) {
      return `${(requests / 1000000).toFixed(1)}M`;
    } else if (requests >= 1000) {
      return `${(requests / 1000).toFixed(1)}K`;
    }
    return requests.toLocaleString();
  };

  if (loading) {
    return (
      <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6">
        <div className="animate-pulse">
          <div className="h-6 bg-gray-200 rounded mb-6 w-40"></div>
          <div className="space-y-4">
            {[...Array(6)].map((_, index) => (
              <div key={index} className="p-4 border border-gray-200 rounded-lg">
                <div className="flex items-center justify-between mb-2">
                  <div className="h-5 bg-gray-200 rounded w-32"></div>
                  <div className="h-6 bg-gray-200 rounded w-20"></div>
                </div>
                <div className="flex items-center justify-between">
                  <div className="flex space-x-4">
                    <div className="h-4 bg-gray-200 rounded w-24"></div>
                    <div className="h-4 bg-gray-200 rounded w-20"></div>
                  </div>
                  <div className="h-4 bg-gray-200 rounded w-12"></div>
                </div>
              </div>
            ))}
          </div>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6">
        <div className="bg-red-50 border border-red-200 rounded-lg p-4">
          <p className="text-red-600 text-sm">Error loading top API users: {error}</p>
        </div>
      </div>
    );
  }

  return (
    <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6">
      <div className="flex items-center justify-between mb-6">
        <h3 className="text-lg font-semibold text-gray-900">Top API Users</h3>
      </div>

      <div className="space-y-4">
        {topUsers.length === 0 ? (
          <div className="text-center py-8 text-gray-500">
            No API users data available
          </div>
        ) : (
          topUsers.map((user, index) => (
            <div key={user.no || index} className="flex items-center justify-between p-4 border border-gray-200 rounded-lg hover:bg-gray-50 transition-colors">
              <div className="flex-1">
                <div className="flex items-center justify-between mb-2">
                  <h4 className="font-medium text-gray-900">{user.company}</h4>
                  <span className={`px-2 py-1 text-xs font-medium rounded-full ${getPlanTypeColor(user.planType.name)}`}>
                    {user.planType.name}
                  </span>
                </div>
                <div className="flex items-center justify-between">
                  <div className="flex items-center space-x-4">
                    <span className="text-sm text-gray-600">
                      Revenue: <span className="font-medium text-gray-900">{formatCurrency(user.revenue)}</span>
                    </span>
                    <span className="text-sm text-gray-600">
                      Requests: <span className="font-medium text-gray-900">{formatRequests(user.apiRequests)}</span>
                    </span>
                  </div>
                  <div className="flex items-center space-x-1 text-green-600">
                    <TrendingUp className="w-4 h-4" />
                    <span className="text-sm font-medium">{user.growth}</span>
                  </div>
                </div>
              </div>
            </div>
          ))
        )}
      </div>
    </div>
  );
};

export default TopAPIUsers;
